import React, { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentAdmin, setCurrentAdmin] = useState(null)
  const [adminRole, setAdminRole] = useState(null)

  // Role permissions configuration
  const rolePermissions = {
    'super-admin': {
      canManageAdmins: true,
      canManageApplications: true,
      canUpdateStatus: true,
      canDelete: true,
      canExport: true,
      canViewAnalytics: true,
      canManageContent: true
    },
    'admin': {
      canManageAdmins: false,
      canManageApplications: true,
      canUpdateStatus: true,
      canDelete: true,
      canExport: true,
      canViewAnalytics: true,
      canManageContent: true
    },
    'editor': {
      canManageAdmins: false,
      canManageApplications: true,
      canUpdateStatus: false,
      canDelete: false,
      canExport: true,
      canViewAnalytics: true,
      canManageContent: true
    },
    'viewer': {
      canManageAdmins: false,
      canManageApplications: false,
      canUpdateStatus: false,
      canDelete: false,
      canExport: false,
      canViewAnalytics: true,
      canManageContent: false
    }
  }

  useEffect(() => {
    const authStatus = localStorage.getItem('shikdar_films_admin_auth')
    const adminUsername = localStorage.getItem('shikdar_films_current_admin')
    const savedRole = localStorage.getItem('shikdar_films_admin_role')
    
    if (authStatus === 'true' && adminUsername) {
      setIsAuthenticated(true)
      setCurrentAdmin(adminUsername)
      setAdminRole(savedRole || 'super-admin')
    }
    setIsLoading(false)
  }, [])

  const hashPassword = (password) => {
    let hash = 0
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(36)
  }

  const sendEmail = (emailData) => {
    const emails = JSON.parse(localStorage.getItem('shikdar_films_email_log') || '[]')
    emails.push({
      id: Date.now(),
      ...emailData,
      timestamp: new Date().toISOString(),
      status: 'sent'
    })
    localStorage.setItem('shikdar_films_email_log', JSON.stringify(emails))
  }

  const login = (username, password) => {
    const defaultAdmin = {
      username: 'admin',
      password: 'admin123',
      role: 'super-admin'
    }

    const customDefaultPassword = localStorage.getItem('shikdar_films_default_admin_password')
    
    if (username === defaultAdmin.username) {
      const validPassword = customDefaultPassword 
        ? hashPassword(password) === customDefaultPassword
        : password === defaultAdmin.password

      if (validPassword) {
        setIsAuthenticated(true)
        setCurrentAdmin(username)
        setAdminRole('super-admin')
        localStorage.setItem('shikdar_films_admin_auth', 'true')
        localStorage.setItem('shikdar_films_current_admin', username)
        localStorage.setItem('shikdar_films_admin_role', 'super-admin')
        return true
      }
    }

    const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
    const admin = admins.find(a => a.username === username && a.isActive)
    
    if (admin && admin.password === hashPassword(password)) {
      setIsAuthenticated(true)
      setCurrentAdmin(username)
      setAdminRole(admin.role)
      localStorage.setItem('shikdar_films_admin_auth', 'true')
      localStorage.setItem('shikdar_films_current_admin', username)
      localStorage.setItem('shikdar_films_admin_role', admin.role)
      return true
    }

    return false
  }

  const logout = () => {
    setIsAuthenticated(false)
    setCurrentAdmin(null)
    setAdminRole(null)
    localStorage.removeItem('shikdar_films_admin_auth')
    localStorage.removeItem('shikdar_films_current_admin')
    localStorage.removeItem('shikdar_films_admin_role')
  }

  const hasPermission = (permission) => {
    if (!adminRole) return false
    return rolePermissions[adminRole]?.[permission] || false
  }

  const value = {
    isAuthenticated,
    isLoading,
    currentAdmin,
    adminRole,
    login,
    logout,
    hasPermission,
    sendEmail,
    rolePermissions
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}